import React from 'react';
import { scopedWorkers, useDash } from '../state';
import { Banner, Grid, Kpi, KpiRow, Panel, Pill, dataTable } from '../ui';
import { RotaImport } from '../RotaImport';
import { useRota } from '../RotaContext';
import { ScopeSwitchers } from './past/ScopePickers';
import { localShiftDay, rotaWeek, shiftHours } from '../../data/rota';
import type { RotaShift } from '../../data/rota';

type RotaView = 'shifts' | 'driving' | 'resilience' | 'compliance';
const fmt = (n: number) => Number(n.toFixed(1));
const clock = (s: RotaShift, end = false) =>
  new Intl.DateTimeFormat('en-GB', { timeZone: s.timezone, hour: '2-digit', minute: '2-digit' }).format(
    new Date(end ? s.plannedEnd : s.plannedStart),
  );
const titles: Record<RotaView, string> = {
  shifts: 'Shift rota',
  driving: 'Driving schedule',
  resilience: 'Overtime & recovery',
  compliance: 'Schedule checks',
};
export const LiveRotaScreen: React.FC<{ kind: RotaView }> = ({ kind }) => {
  const d = useDash();
  const rota = useRota();
  const workers = scopedWorkers(d);
  const userIds = new Set(workers.map((w) => w.userId));
  const shifts = rota.shifts.filter((s) => userIds.has(s.userId));
  const stats = workers.map((w) => ({ w, week: rotaWeek(shifts.filter((s) => s.userId === w.userId)) }));
  const covered = stats.filter((x) => shifts.some((s) => s.userId === x.w.userId));
  const current = rotaWeek(shifts);
  const weekLabel = `${current.start} to ${new Date(Date.parse(current.end) - 86400000).toISOString().slice(0, 10)}`;
  const rows = [...shifts].sort((a, b) => b.plannedStart.localeCompare(a.plannedStart));
  const name = (id: string) => rota.workers.find((w) => w.id === id)?.name ?? 'Worker';
  const driving = covered.filter((x) => x.week.drivingKm !== null);
  return (
    <div className='flex flex-col gap-5'>
      {kind === 'shifts' && <RotaImport />}
      <ScopeSwitchers />
      {!shifts.length ? (
        <Panel
          title='No shifts in this scope'
          sub='Import a rota for this client to see shift hours, rest gaps, overtime and driving.'
        >
          {kind !== 'shifts' && (
            <button type='button' className='text-sm text-brand hover:underline' onClick={() => d.go('shifts')}>
              Open Shifts & Sleep to import a rota →
            </button>
          )}
        </Panel>
      ) : (
        <>
          <div>
            <h2 className='text-lg font-semibold'>{titles[kind]}</h2>
            <p className='mt-1 text-xs text-muted-foreground'>
              Week {weekLabel} · {covered.length} of {workers.length} workers have rota data. Hours below are scheduled;
              actual hours appear only when clock times were supplied.
            </p>
          </div>
          <KpiRow>
            <Kpi label='Workers with rota' value={`${covered.length}/${workers.length}`} sub='This scope' tone='info' />
            <Kpi label='Scheduled hours' value={`${fmt(current.hours)}h`} sub='This week, excluding breaks' />
            <Kpi
              label='Scheduled overtime'
              value={`${fmt(covered.reduce((n, x) => n + x.week.overtime, 0))}h`}
              sub='Above each worker’s contracted hours'
              tone={covered.some((x) => x.week.overtime > 0) ? 'warn' : 'good'}
            />
            <Kpi
              label='Short rest gaps'
              value={covered.reduce((n, x) => n + x.week.shortRestCount, 0)}
              sub='Scheduled gaps below 11h · review flag'
              tone={covered.some((x) => x.week.shortRestCount > 0) ? 'warn' : 'good'}
            />
          </KpiRow>
          {kind === 'driving' ? (
            <>
              <Banner tone='info'>
                Driving distance is shown only when supplied by the rota. Kilometres do not establish driving-time
                compliance.
              </Banner>
              <Panel title='Scheduled driving' sub='This week · missing distances are shown as unavailable'>
                <div className={dataTable.wrap}>
                  <table className={dataTable.table}>
                    <thead>
                      <tr>
                        {['Worker', 'Distance', 'Days with distance', 'FED'].map((h) => (
                          <th key={h} className={dataTable.th}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {driving.map(({ w, week }) => (
                        <tr key={w.init}>
                          <td className={dataTable.td}>{w.name}</td>
                          <td className={dataTable.td}>{fmt(week.drivingKm!)} km</td>
                          <td className={dataTable.td}>
                            {new Set(week.shifts.filter((s) => (s.drivingKm ?? 0) > 0).map(localShiftDay)).size}
                          </td>
                          <td className={dataTable.td}>{w.fed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!driving.length && (
                  <p className='mt-3 text-sm text-muted-foreground'>No driving distances supplied for this week.</p>
                )}
              </Panel>
            </>
          ) : kind === 'resilience' ? (
            <>
              <Banner tone='info'>
                This view compares scheduled overtime with current wearable fatigue. It does not infer an individual
                resilience score or a causal response to overtime.
              </Banner>
              <Panel title='Overtime and current fatigue' sub='Schedule context alongside current wearable readings'>
                <div className={dataTable.wrap}>
                  <table className={dataTable.table}>
                    <thead>
                      <tr>
                        {['Worker', 'Scheduled', 'Contracted', 'Scheduled OT', 'Actual recorded', 'FED', 'Sleep'].map(
                          (h) => (
                            <th key={h} className={dataTable.th}>
                              {h}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {covered
                        .sort((a, b) => b.week.overtime - a.week.overtime)
                        .map(({ w, week }) => (
                          <tr key={w.init}>
                            <td className={dataTable.td}>{w.name}</td>
                            <td className={dataTable.td}>{fmt(week.hours)}h</td>
                            <td className={dataTable.td}>{week.contracted}h</td>
                            <td className={dataTable.td}>{fmt(week.overtime)}h</td>
                            <td className={dataTable.td}>
                              {week.actualHours === null
                                ? 'Not supplied'
                                : `${fmt(week.actualHours)}h (${week.actualCount} shifts)`}
                            </td>
                            <td className={dataTable.td}>{w.fed}</td>
                            <td className={dataTable.td}>{w.slp}h</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          ) : kind === 'compliance' ? (
            <>
              <Banner tone='info'>
                These are schedule review flags. Actual working-time compliance needs complete time records, the
                relevant reference period and applicable exceptions.
              </Banner>
              <Panel title='Rest and hours review' sub='This week · no waiver or approval records are inferred'>
                <div className={dataTable.wrap}>
                  <table className={dataTable.table}>
                    <thead>
                      <tr>
                        {['Worker', 'Scheduled hours', 'Shortest gap', 'Gaps <11h', 'Consecutive starts', 'Review'].map(
                          (h) => (
                            <th key={h} className={dataTable.th}>
                              {h}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {covered.map(({ w, week }) => (
                        <tr key={w.init}>
                          <td className={dataTable.td}>{w.name}</td>
                          <td className={dataTable.td}>{fmt(week.hours)}h</td>
                          <td className={dataTable.td}>
                            {week.shortestRest === null ? 'Not available' : `${fmt(week.shortestRest)}h`}
                          </td>
                          <td className={dataTable.td}>{week.shortRestCount}</td>
                          <td className={dataTable.td}>{week.longestRun} days</td>
                          <td className={dataTable.td}>
                            <Pill tone={week.shortRestCount > 0 || week.hours > 48 ? 'warn' : 'muted'}>
                              {week.shortRestCount > 0 || week.hours > 48
                                ? 'Review schedule'
                                : 'No flags in supplied schedule'}
                            </Pill>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          ) : (
            <>
              <Grid cols={2}>
                <Panel title='Weekly hours & sleep' sub='Scheduled hours alongside latest wearable sleep'>
                  <div className={dataTable.wrap}>
                    <table className={dataTable.table}>
                      <thead>
                        <tr>
                          {['Worker', 'Scheduled', 'Actual recorded', 'Latest sleep'].map((h) => (
                            <th key={h} className={dataTable.th}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {covered.map(({ w, week }) => (
                          <tr key={w.init}>
                            <td className={dataTable.td}>
                              <button
                                type='button'
                                onClick={() =>
                                  d.patch({ activeWorker: w.init, drillScope: 'individual', screen: 'worker' })
                                }
                                className='text-brand hover:underline'
                              >
                                {w.name}
                              </button>
                            </td>
                            <td className={dataTable.td}>{fmt(week.hours)}h</td>
                            <td className={dataTable.td}>
                              {week.actualHours === null ? 'Not supplied' : `${fmt(week.actualHours)}h`}
                            </td>
                            <td className={dataTable.td}>{w.slp}h</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Panel>
                <Panel title='Shift timing' sub='This week · classified using the shift’s local start time'>
                  <div className='flex flex-col gap-3'>
                    {['Night', 'Early', 'Day'].map((label) => {
                      const count = current.shifts.filter((s) => {
                        const hour = Number(clock(s).slice(0, 2));
                        return (hour >= 22 || hour < 4 ? 'Night' : hour < 7 ? 'Early' : 'Day') === label;
                      }).length;
                      return (
                        <div key={label} className='flex justify-between rounded-lg bg-muted px-4 py-3 text-sm'>
                          <span>{label} starts</span>
                          <strong>{count}</strong>
                        </div>
                      );
                    })}
                  </div>
                </Panel>
              </Grid>
              <Panel
                title='Shift records'
                sub={`${rows.length} shifts · most recent first · local times use each record’s timezone`}
              >
                <div className={dataTable.wrap}>
                  <table className={dataTable.table + ' min-w-[780px]'}>
                    <thead>
                      <tr>
                        {['Worker', 'Date', 'Start', 'End', 'Net hours', 'Break', 'Time zone', 'Record', 'Source'].map(
                          (h) => (
                            <th key={h} className={dataTable.th}>
                              {h}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(0, 500).map((s) => (
                        <tr key={s.id}>
                          <td className={dataTable.td}>{name(s.userId)}</td>
                          <td className={dataTable.td}>{localShiftDay(s)}</td>
                          <td className={dataTable.td}>{clock(s)}</td>
                          <td className={dataTable.td}>
                            {clock(s, true)}
                            {localShiftDay(s) !==
                              new Intl.DateTimeFormat('en-CA', { timeZone: s.timezone }).format(
                                new Date(s.plannedEnd),
                              ) && <span className='ml-1 text-xs text-muted-foreground'>(next day)</span>}
                          </td>
                          <td className={dataTable.td}>{fmt(shiftHours(s) ?? 0)}h</td>
                          <td className={dataTable.td}>{s.breakMinutes}m</td>
                          <td className={dataTable.td}>{s.timezone}</td>
                          <td className={dataTable.td}>{s.actualEnd ? 'Actual times supplied' : 'Planned only'}</td>
                          <td className={dataTable.td}>
                            <Pill tone={s.source === 'demo' ? 'warn' : 'info'}>
                              {s.source === 'demo' ? 'Demo rota' : 'Imported'}
                            </Pill>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {rows.length > 500 && (
                  <p className='mt-2 text-xs text-muted-foreground'>Showing the latest 500 shifts.</p>
                )}
              </Panel>
            </>
          )}
        </>
      )}
    </div>
  );
};
